import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Video, Save, ExternalLink, Plus, X, Star, Shield, Crosshair } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";

const emptyEvent = () => ({ id: crypto.randomUUID(), minute: "", text: "" });

export default function MatchReportDialog({ open, onOpenChange, convocation, players = [], onSaved }) {
  const [summary, setSummary] = useState("");
  const [veoUrl, setVeoUrl] = useState("");
  const [veoNotes, setVeoNotes] = useState("");
  const [tacticalDefense, setTacticalDefense] = useState("");
  const [tacticalMidfield, setTacticalMidfield] = useState("");
  const [tacticalAttack, setTacticalAttack] = useState("");
  const [mvpPlayerId, setMvpPlayerId] = useState("");
  const [events, setEvents] = useState([]);

  useEffect(() => {
    if (!convocation) return;
    setSummary(convocation.report_summary || "");
    setVeoUrl(convocation.veo_video_url || "");
    setVeoNotes(convocation.veo_notes || "");
    setTacticalDefense(convocation.tactical_defense || "");
    setTacticalMidfield(convocation.tactical_midfield || "");
    setTacticalAttack(convocation.tactical_attack || "");
    setMvpPlayerId(convocation.mvp_player_id || "");
    setEvents(
      (convocation.salient_events || []).length > 0
        ? convocation.salient_events.map((e) => ({ id: e.id || crypto.randomUUID(), ...e }))
        : []
    );
  }, [convocation]);

  if (!convocation) return null;

  const calledPlayers = (convocation.player_ids || [])
    .map((pid) => players.find((p) => p.id === pid))
    .filter(Boolean)
    .sort((a, b) => (a.jersey_number || 99) - (b.jersey_number || 99));

  const updateEvent = (id, patch) =>
    setEvents((evs) => evs.map((e) => (e.id === id ? { ...e, ...patch } : e)));
  const removeEvent = (id) => setEvents((evs) => evs.filter((e) => e.id !== id));

  const save = async () => {
    try {
      await api.post(`/convocations/${convocation.id}/report`, {
        summary,
        veo_video_url: veoUrl,
        veo_notes: veoNotes,
        tactical_defense: tacticalDefense,
        tactical_midfield: tacticalMidfield,
        tactical_attack: tacticalAttack,
        mvp_player_id: mvpPlayerId || null,
        salient_events: events
          .filter((e) => e.text?.trim())
          .map((e) => ({ id: e.id, minute: e.minute, text: e.text.trim() })),
      });
      toast.success("Report partita salvato");
      onOpenChange(false);
      if (onSaved) onSaved();
    } catch {
      toast.error("Errore nel salvataggio");
    }
  };

  const isValidUrl = veoUrl && /^https?:\/\//i.test(veoUrl);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Analisi post-match — {convocation.title}</DialogTitle>
          <DialogDescription className="sr-only">
            Annota analisi tattica, MVP, eventi salienti e link Veo Cam per la partita.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-5">
          <div>
            <Label>Commento generale partita</Label>
            <Textarea
              rows={4}
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              placeholder="Sintesi prestazione, momenti chiave, sensazioni..."
              data-testid="report-summary-input"
            />
          </div>

          {/* Tactical zones */}
          <div className="space-y-3 p-4 rounded-xl border border-slate-200">
            <div className="flex items-center gap-2 text-slate-700 font-semibold">
              <Shield size={16} className="text-sky-500" /> Valutazione tattica per reparto
            </div>
            <div>
              <Label className="text-xs">Difesa</Label>
              <Textarea
                rows={2}
                value={tacticalDefense}
                onChange={(e) => setTacticalDefense(e.target.value)}
                placeholder="Reparto difensivo: posizionamento, marcature, transizione negativa..."
                data-testid="tactical-defense-input"
              />
            </div>
            <div>
              <Label className="text-xs">Centrocampo</Label>
              <Textarea
                rows={2}
                value={tacticalMidfield}
                onChange={(e) => setTacticalMidfield(e.target.value)}
                placeholder="Reparto mediano: pressing, costruzione, filtro..."
                data-testid="tactical-midfield-input"
              />
            </div>
            <div>
              <Label className="text-xs">Attacco</Label>
              <Textarea
                rows={2}
                value={tacticalAttack}
                onChange={(e) => setTacticalAttack(e.target.value)}
                placeholder="Reparto offensivo: finalizzazione, movimenti, ampiezza..."
                data-testid="tactical-attack-input"
              />
            </div>
          </div>

          {/* MVP */}
          <div className="p-4 rounded-xl border border-amber-200 bg-amber-50/40">
            <Label className="flex items-center gap-2 mb-2 text-amber-700 font-semibold">
              <Star size={16} /> MVP della partita
            </Label>
            <Select value={mvpPlayerId || "__none__"} onValueChange={(v) => setMvpPlayerId(v === "__none__" ? "" : v)}>
              <SelectTrigger data-testid="mvp-select">
                <SelectValue placeholder="Seleziona MVP" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">— Nessuno —</SelectItem>
                {calledPlayers.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    #{p.jersey_number ?? "—"} {p.last_name} {p.first_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Salient events */}
          <div className="p-4 rounded-xl border border-slate-200">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2 text-slate-700 font-semibold">
                <Crosshair size={16} className="text-violet-500" /> Eventi salienti
              </div>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => setEvents((evs) => [...evs, emptyEvent()])}
                data-testid="add-event-btn"
              >
                <Plus size={13} className="mr-1" /> Evento
              </Button>
            </div>
            <div className="space-y-2">
              {events.map((e) => (
                <div key={e.id} className="flex items-center gap-2" data-testid={`event-${e.id}`}>
                  <Input
                    placeholder="min"
                    value={e.minute}
                    onChange={(ev) => updateEvent(e.id, { minute: ev.target.value })}
                    className="w-16 text-center"
                  />
                  <Input
                    placeholder="Es. corner battuto, sostituzione, gol annullato..."
                    value={e.text}
                    onChange={(ev) => updateEvent(e.id, { text: ev.target.value })}
                    className="flex-1"
                  />
                  <Button size="icon" variant="ghost" onClick={() => removeEvent(e.id)}>
                    <X size={14} />
                  </Button>
                </div>
              ))}
              {events.length === 0 && (
                <p className="text-xs text-slate-400 italic">
                  Nessun evento. Aggiungi i momenti chiave (gol, ammonizioni, sostituzioni, episodi).
                </p>
              )}
            </div>
          </div>

          {/* Veo Cam */}
          <div className="space-y-3 p-4 rounded-xl border border-violet-200 bg-violet-50/30">
            <div className="flex items-center gap-2 text-violet-700 font-semibold">
              <Video size={16} /> Veo Cam — registrazione partita
            </div>
            <div>
              <Label className="text-xs">Link pubblico Veo</Label>
              <Input
                type="url"
                value={veoUrl}
                onChange={(e) => setVeoUrl(e.target.value)}
                placeholder="https://app.veo.co/matches/..."
                data-testid="veo-url-input"
              />
              {isValidUrl && (
                <a
                  href={veoUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 mt-2 text-xs text-violet-600 hover:underline"
                >
                  <ExternalLink size={12} /> Apri video Veo
                </a>
              )}
            </div>
            <div>
              <Label className="text-xs">Spunti dal video</Label>
              <Textarea
                rows={2}
                value={veoNotes}
                onChange={(e) => setVeoNotes(e.target.value)}
                placeholder="Es. minuto 25 azione corner, minuto 47 errore difensivo..."
              />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Annulla
          </Button>
          <Button
            onClick={save}
            className="bg-sky-500 hover:bg-sky-600"
            data-testid="save-match-report-btn"
          >
            <Save size={14} className="mr-1" /> Salva analisi
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
